/**
 * One rule, opened.
 *
 * The list answers "what is in the rulebook". This answers the question that
 * follows it - what does this rule actually match, what does it tell a clinic,
 * and what provision is behind it - and, when the rule is in a draft, lets those
 * be corrected.
 *
 * Correcting is why this exists. A mined pattern is ninety per cent right far
 * more often than it is useless, and the only answers used to be "accept it as
 * written" or "reject and mine again" - neither of which is what somebody wants
 * when the wording is fine and one alternative is too broad.
 *
 * Two things stay fixed:
 *
 *   THE SLUG. It is the rule's identity across versions - what a finding names,
 *   and what a reviewer's "considered, and fine" decision is remembered against.
 *   Renaming it would silently reopen every dismissal made under the old name.
 *
 *   A LIVE RULE. The active set is what stored verdicts were produced by, so
 *   nothing here is editable until the rule is in a draft.
 */
import { useState } from 'react';
import { ApiError, del, patch, type Rule } from '../api.ts';
import { Pill, jaDate } from './ui.tsx';
import { Modal, ModalForm } from './Modal.tsx';
import { Field } from './EditPanel.tsx';
import { Select } from './Select.tsx';
import { Textarea } from './Textarea.tsx';

const SEVERITY_LABEL: Record<string, string> = {
  REVIEW: '要確認',
  UNSUPPORTED: '根拠不足',
};

export function RuleDetail({
  rule,
  editable,
  onClose,
  onChanged,
}: {
  rule: Rule;
  /** True while the rule is in a draft. A live rule is a record, not a document. */
  editable: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState({
    title: rule.title,
    guidance: rule.guidance,
    fix: rule.fix,
    severity: String(rule.severity),
    patterns: rule.patterns.join('\n'),
    law: rule.law,
    article: rule.article,
  });
  const [error, setError] = useState<string | null>(null);

  /*
   * One pattern per line, which is how they are stored and how they are read.
   * A single alternation would be shorter and unreviewable - the point of the
   * array is that a person can look at each alternative and delete the one that
   * is too broad.
   */
  const patterns = form.patterns
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <>
      <Modal open onClose={onClose} title={rule.title} caption={rule.slug} wide>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <dl className="factgrid" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
          <div className="factgrid__row">
            <dt>区分</dt>
            <dd>
              <Pill tone={rule.severity === 'REVIEW' ? 'warn' : 'muted'}>
                {SEVERITY_LABEL[rule.severity] ?? rule.severity}
              </Pill>
            </dd>
          </div>
          <div className="factgrid__row">
            <dt>根拠</dt>
            <dd>
              {rule.law}
              {rule.article && <span className="muted small">（{rule.article}）</span>}
            </dd>
          </div>
          <div className="factgrid__row">
            <dt>適用開始</dt>
            <dd>{rule.effective_from ? jaDate(rule.effective_from) : <span className="muted">—</span>}</dd>
          </div>
        </dl>

        <p className="form__section">クリニックに表示される指摘</p>
        <p style={{ marginTop: 0 }}>{rule.guidance}</p>
        {rule.fix && (
          <p className="small" style={{ marginTop: 0 }}>
            修正案：{rule.fix}
          </p>
        )}

        <p className="form__section">検出パターン</p>
        <ul className="patternlist">
          {rule.patterns.map((pattern) => (
            <li key={pattern}>
              <code className="small">{pattern}</code>
            </li>
          ))}
        </ul>

        {/*
          The provision it was mined from, quoted. A hand-written rule has none,
          and the difference matters: a rule with a citation can be checked
          against the law, one without can only be trusted.
        */}
        {rule.source_excerpt ? (
          <>
            <p className="form__section">
              出典{rule.source_heading ? `：${rule.source_heading}` : ''}
            </p>
            <p className="small" style={{ whiteSpace: 'pre-wrap', marginTop: 0 }}>
              {rule.source_excerpt}
            </p>
          </>
        ) : (
          <p className="muted small">
            条文の引用がありません（初期ルール、または手動で追加されたルールです）。
            根拠は上の記載のみで、条文との突き合わせはできません。
          </p>
        )}

        <div className="editpanel__actions">
          {editable ? (
            <>
              <button className="btn btn--sm" type="button" onClick={() => setEditing(true)}>
                編集
              </button>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => setRemoving(true)}
              >
                削除
              </button>
            </>
          ) : (
            <span className="muted small">
              本番のルールです。変更するには下書きを作成し、その下書きの中で編集します。
            </span>
          )}
          <button className="btn btn--ghost btn--sm" type="button" onClick={onClose}>
            閉じる
          </button>
        </div>
      </Modal>

      {editing && (
        <ModalForm
          open
          onClose={() => setEditing(false)}
          title="ルールを編集"
          caption={`${rule.slug}：識別子は変更できません（過去の指摘との対応が切れるため）`}
          submitLabel="保存する"
          wide
          onSubmit={async () => {
            if (patterns.length === 0) throw new Error('検出パターンを1つ以上入力してください');
            for (const pattern of patterns) {
              try {
                new RegExp(pattern);
              } catch {
                throw new Error(`正規表現が不正です: ${pattern}`);
              }
            }
            try {
              await patch(`/admin/rulebook/rules/${rule.id}`, {
                title: form.title.trim(),
                guidance: form.guidance.trim(),
                fix: form.fix.trim(),
                severity: form.severity,
                patterns,
                law: form.law.trim(),
                article: form.article.trim(),
              });
            } catch (err) {
              throw err instanceof ApiError ? new Error(err.message) : err;
            }
            setEditing(false);
            onChanged();
            onClose();
          }}
        >
          <Field label="名称" htmlFor="rule-title" required>
            <input
              id="rule-title"
              value={form.title}
              maxLength={120}
              required
              onChange={(event) => setForm({ ...form, title: event.target.value })}
            />
          </Field>
          <Field label="区分" htmlFor="rule-severity">
            <Select
              id="rule-severity"
              value={form.severity}
              onChange={(severity) => setForm({ ...form, severity })}
              options={[
                { value: 'REVIEW', label: '要確認（表現の是正が必要）' },
                { value: 'UNSUPPORTED', label: '根拠不足（裏づけの提示が必要）' },
              ]}
            />
          </Field>
          <Field
            label="指摘文"
            htmlFor="rule-guidance"
            span="full"
            required
            hint="クリニックの画面にそのまま出ます。何が問題かを一文で。"
          >
            <Textarea
              id="rule-guidance"
              minRows={2}
              maxLength={1000}
              value={form.guidance}
              onChange={(event) => setForm({ ...form, guidance: event.target.value })}
            />
          </Field>
          <Field label="修正案" htmlFor="rule-fix" span="full" hint="どう書き換えれば通るか。任意。">
            <Textarea
              id="rule-fix"
              minRows={2}
              maxLength={500}
              value={form.fix}
              onChange={(event) => setForm({ ...form, fix: event.target.value })}
            />
          </Field>
          <Field
            label="検出パターン（1行に1つ）"
            htmlFor="rule-patterns"
            span="full"
            required
            hint="日本語の正規表現。「効果」のような一般語だけのパターンは、正しい文まで指摘します。"
          >
            <Textarea
              id="rule-patterns"
              minRows={3}
              maxLength={4000}
              value={form.patterns}
              onChange={(event) => setForm({ ...form, patterns: event.target.value })}
            />
          </Field>
          <Field label="根拠となる法令" htmlFor="rule-law" required>
            <input
              id="rule-law"
              value={form.law}
              maxLength={200}
              required
              onChange={(event) => setForm({ ...form, law: event.target.value })}
            />
          </Field>
          <Field label="条項" htmlFor="rule-article">
            <input
              id="rule-article"
              value={form.article}
              maxLength={300}
              onChange={(event) => setForm({ ...form, article: event.target.value })}
            />
          </Field>
        </ModalForm>
      )}

      {removing && (
        <ModalForm
          open
          onClose={() => setRemoving(false)}
          title="このルールを下書きから外しますか"
          caption={`${rule.slug}：本番のセットは変わりません。`}
          submitLabel="削除する"
          danger
          onSubmit={async () => {
            try {
              await del(`/admin/rulebook/rules/${rule.id}`);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : '削除できませんでした');
              throw err;
            }
            setRemoving(false);
            onChanged();
            onClose();
          }}
        >
          <p className="field--full" style={{ margin: 0 }}>
            この下書きを有効化したとき、このルールは記事チェックに使われなくなります。
          </p>
        </ModalForm>
      )}
    </>
  );
}
